import React, { useState, useEffect } from 'react';
import api from '../api';

// ILS pnl values come from server in shekels (fixIlsPnL); display as whole shekels
const fmt = (n, currency = 'USD') => {
  if (n === undefined || n === null || isNaN(n)) return '-';
  if (currency === 'ILS') {
    try {
      return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n));
    } catch { return `₪${Math.round(n).toLocaleString('he-IL')}`; }
  }
  return new Intl.NumberFormat('he-IL', { style: 'currency', currency, minimumFractionDigits: 2 }).format(n);
};
const fmtPct = (n) => (!n && n !== 0) ? '-' : (n >= 0 ? `+${n.toFixed(2)}%` : `${n.toFixed(2)}%`);

export default function Reports() {
  const [data, setData] = useState(null);
  const [txs, setTxs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ symbol: '', from: '', to: '' });

  const load = async (overrideFilters) => {
    const f = overrideFilters || filters;
    setLoading(true);
    try {
      const params = {};
      if (f.symbol) params.symbol = f.symbol;
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;
      const [summaryRes, txRes] = await Promise.all([
        api.get('/reports/summary', { params }),
        api.get('/reports/transactions', { params }),
      ]);
      setData(summaryRes.data);
      setTxs(txRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="page">
      <h1 className="page-title">דוחות</h1>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>🔍 סינון</div>
        <div className="form-grid-3">
          <div className="form-group">
            <label className="form-label">סימול מניה</label>
            <input className="form-input" placeholder="לדוגמה: AAPL" value={filters.symbol}
              onChange={e => setFilters({...filters, symbol: e.target.value.toUpperCase()})} />
          </div>
          <div className="form-group">
            <label className="form-label">מתאריך</label>
            <input className="form-input" type="date" value={filters.from}
              onChange={e => setFilters({...filters, from: e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">עד תאריך</label>
            <input className="form-input" type="date" value={filters.to}
              onChange={e => setFilters({...filters, to: e.target.value})} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button className="btn btn-primary btn-sm" onClick={() => load()}>הצג דוח</button>
          <button className="btn btn-secondary btn-sm" onClick={() => { const cleared = { symbol: '', from: '', to: '' }; setFilters(cleared); load(cleared); }}>נקה</button>
        </div>
      </div>

      {loading ? <div className="loading">טוען...</div> : data && (
        <>
          <div className="pnl-banner" style={{ marginBottom: 20 }}>
            <div className="stats-grid" style={{ marginBottom: 0 }}>
              <div className="stat-box">
                <div className="stat-label">סה"כ פוזיציות</div>
                <div className="stat-value">{data.summary.totalInstances}</div>
              </div>
              <div className="stat-box">
                <div className="stat-label">פוזיציות סגורות</div>
                <div className="stat-value">{data.summary.closedInstances}</div>
              </div>
              <div className="stat-box">
                <div className="stat-label">רווח/הפסד נקי כולל</div>
                <div className="stat-value" style={{ color: data.summary.totalNetProfit >= 0 ? '#7fffd4' : '#ffb3c1' }}>
                  {fmt(data.summary.totalNetProfit)}
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-label">תשואה ממוצעת</div>
                <div className="stat-value" style={{ color: data.summary.avgRoi >= 0 ? '#7fffd4' : '#ffb3c1' }}>
                  {fmtPct(data.summary.avgRoi)}
                </div>
              </div>
            </div>
          </div>

          {data.instances.length === 0 ? (
            <div className="empty"><div className="empty-icon">📊</div><div className="empty-text">לא נמצאו פוזיציות</div></div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', fontWeight: 700 }}>כל הפוזיציות</div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>מניה</th><th>פלטפורמה</th><th>מטבע</th><th>סטטוס</th>
                      <th>נקנה</th><th>נמכר</th><th>עמלות</th><th>רווח נקי</th><th>תשואה</th><th>תאריך פתיחה</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.instances.map(inst => {
                      const pnl = inst.pnl || {};
                      const profit = pnl.netProfit || 0;
                      return (
                        <tr key={inst.id}>
                          <td><strong>{inst.stock_symbol}</strong></td>
                          <td style={{ color: 'var(--muted)' }}>{inst.platform_name}</td>
                          <td><span className="tag">{inst.base_currency}</span></td>
                          <td><span className={`badge ${inst.status}`}>{inst.status === 'open' ? 'פתוחה' : 'סגורה'}</span></td>
                          <td>{fmt(pnl.totalBoughtCost, inst.base_currency)}</td>
                          <td>{fmt(pnl.totalSoldRevenue, inst.base_currency)}</td>
                          <td className="loss">{fmt(pnl.totalCommissions, inst.base_currency)}</td>
                          <td><strong className={profit >= 0 ? 'profit' : 'loss'}>{fmt(profit, inst.base_currency)}</strong></td>
                          <td className={(pnl.roi||0) >= 0 ? 'profit' : 'loss'}>{fmtPct(pnl.roi)}</td>
                          <td style={{ color: 'var(--muted)', fontSize: '.85rem' }}>{new Date(inst.created_at).toLocaleDateString('he-IL')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* פעולות בטווח — כל הקניות/מכירות */}
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 20 }}>
            <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>פעולות בטווח</span>
              <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: '.85rem' }}>{txs ? `${txs.length} פעולות` : ''}</span>
            </div>
            {!txs || txs.length === 0 ? (
              <div className="empty" style={{ padding: '32px 0' }}><div className="empty-icon">📋</div><div className="empty-text">לא נמצאו פעולות בטווח</div></div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>תאריך</th><th>פוזיציה</th><th>פעולה</th><th>כמות</th><th>מחיר</th><th>סה"כ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txs.map(t => {
                      const isILS = t.currency === 'ILS';
                      const total = isILS ? Math.round(t.quantity * t.price_per_share / 100) : t.quantity * t.price_per_share;
                      return (
                        <tr key={t.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{new Date(t.transaction_date).toLocaleDateString('he-IL')}</td>
                          <td><strong>{t.stock_symbol}</strong></td>
                          <td><span className={t.type === 'buy' ? 'profit' : 'loss'}>{t.type === 'buy' ? '🟢 קנייה' : '🔴 מכירה'}</span></td>
                          <td>{parseFloat(t.quantity.toFixed(4))}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{isILS ? `${Math.round(t.price_per_share).toLocaleString('he-IL')} אג'` : fmt(t.price_per_share, t.currency)}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmt(total, t.currency)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
